<script lang="ts" setup>
import { ref } from "vue";
import { DxFileUploader } from "devextreme-vue/file-uploader";
import type { OnDropZoneProps, OnValueChangedProps } from "./types";
import IconTrash from "@/assets/icons-svg/icon-trash.svg";

type Props = {
  file: string | null;
  multiple?: boolean;
};

type Emits = {
  (e: "onFile", file: File | File[] | null): void;
};

const emit = defineEmits<Emits>();
const props = defineProps<Props>();

const source = ref(props.file);
const dragging = ref(false);

const onRemoveFile = () => {
  emit("onFile", props.multiple ? null : []);

  source.value = null;
};

const onValueChange = (e: OnValueChangedProps) => {
  emit("onFile", props.multiple ? e.value : e.value[0]);

  const fileReader = new FileReader();

  fileReader.onload = () => {
    dragging.value = false;

    source.value = fileReader.result as string;
  };

  fileReader.readAsDataURL(e.value[0]);
};

const onUploadStarted = () => {
  source.value = "";
};

const onDropZoneEnter = (e: OnDropZoneProps) => {
  if (e.dropZoneElement.id === "dropzone-external") {
    dragging.value = true;
  }
};

const onDropZoneLeave = (e: OnDropZoneProps) => {
  if (e.dropZoneElement.id === "dropzone-external") {
    dragging.value = false;
  }
};
</script>

<template>
  <div
    class="flex p-3 group rounded relative bg-gray-50 transition-all duration-300 ease-in-out border border-solid hover:border-gray-200 hover:dark:border-gray-500 dark:bg-gray-700"
    :class="
      dragging
        ? 'border-gray-200 dark:border-gray-500'
        : 'border-gray-50 dark:border-gray-700'
    "
  >
    <div
      id="dropzone-external"
      class="flex w-full relative cursor-pointer items-center justify-center rounded bg-white dark:bg-gray-800"
    >
      <img
        alt=""
        :src="source"
        v-if="source"
        class="w-full h-full rounded object-cover"
      />

      <div
        v-if="source"
        class="flex rounded items-center justify-center absolute top-0 left-0 w-full h-full opacity-0 pointer-events-none transition-all duration-300 ease-in-out bg-transparent group-hover:bg-black/50 group-hover:opacity-100 group-hover:pointer-events-auto"
      >
        <IconTrash class="text-error-400" @click.stop="onRemoveFile()" />
      </div>

      <div v-if="!source" class="flex">
        <img alt="" src="/assets/svgs/icon-camera.svg" />
      </div>
    </div>

    <DxFileUploader
      id="file-uploader"
      v-bind="[$props, $attrs]"
      :visible="false"
      drop-zone="#dropzone-external"
      dialog-trigger="#dropzone-external"
      @value-changed="onValueChange"
      @upload-started="onUploadStarted"
      @drop-zone-enter="onDropZoneEnter"
      @drop-zone-leave="onDropZoneLeave"
    />
  </div>
</template>
